import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchUserNotifications,
  markNotificationAsRead,
  deleteNotification,
  createNotification,
} from "../../api";

// Thunks

// In-flight promise to dedupe concurrent fetches
let getUserNotificationsInFlight = null;

// Create a new notification
export const addNotification = createAsyncThunk(
  "notifications/addNotification",
  async (notificationData, { rejectWithValue }) => {
    try {
      const data = await createNotification(notificationData);
      return data.notification; // Assuming API returns the created notification object
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch notifications for a user
export const getUserNotifications = createAsyncThunk(
  "notifications/getUserNotifications",
  async (userId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated;
      const existing = state.notifications?.notifications || [];
      const fetchedAt = state.notifications?.fetchedAt || 0;
      const isFresh = Date.now() - fetchedAt < 5 * 60 * 1000; // 5 minutes

      // Gate: only fetch if authenticated and data not present or stale
      if (!isAuthenticated && existing.length > 0) {
        return existing;
      }
      if (isAuthenticated && existing.length > 0 && isFresh) {
        return existing;
      }

      if (getUserNotificationsInFlight) {
        const reused = await getUserNotificationsInFlight;
        return reused.notifications || reused; // support both shapes
      }
      getUserNotificationsInFlight = fetchUserNotifications(userId);
      const data = await getUserNotificationsInFlight;
      return data.notifications; // The response structure must be aligned with this
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    } finally {
      getUserNotificationsInFlight = null;
    }
  }
);

// Mark a notification as read
export const markAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async (notificationId, { rejectWithValue }) => {
    try {
      const data = await markNotificationAsRead(notificationId);
      return data.notification; // Assuming API returns the updated notification
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete a notification
export const deleteNotificationThunk = createAsyncThunk(
  "notifications/deleteNotification",
  async (notificationId, { rejectWithValue }) => {
    try {
      await deleteNotification(notificationId);
      return { notificationId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Slice
const notificationSlice = createSlice({
  name: "notifications",
  initialState: {
    notifications: [],
    adminNotifications: [], // Holds all fetched admin notifications
    loading: false,
    error: null,
    fetchedAt: null,
  },
  reducers: {
    // Add real-time notification
    addRealTimeNotification: (state, action) => {
      state.notifications.unshift(action.payload);
    },
    // Clear notifications
    clearNotifications: (state) => {
      state.notifications = [];
      state.fetchedAt = null;
    },
    // Mark all as read
    markAllAsRead: (state) => {
      state.notifications.forEach(notification => {
        notification.isRead = true;
      });
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch user notifications
      .addCase(getUserNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserNotifications.fulfilled, (state, action) => {
        state.notifications = action.payload;
        state.loading = false;
        state.fetchedAt = Date.now();
      })
      .addCase(getUserNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Mark as read
      .addCase(markAsRead.fulfilled, (state, action) => {
        const index = state.notifications.findIndex(
          (n) => n._id === action.payload._id
        );
        if (index !== -1) {
          state.notifications[index].isRead = true;
        }
      })

      // Delete notification
      .addCase(deleteNotificationThunk.fulfilled, (state, action) => {
        state.notifications = state.notifications.filter(
          (n) => n._id !== action.payload.notificationId
        );
      })

      // Add Notification
      .addCase(addNotification.fulfilled, (state, action) => {
        if (action.payload) {
          state.notifications.unshift(action.payload);
        }
      });
  },
});

export const { addRealTimeNotification, clearNotifications, markAllAsRead } = notificationSlice.actions;

export default notificationSlice.reducer;
